// src/utils/sexoStats.ts
//
// Cálculo de promedios / conteos / GDP desglosados por sexo (Macho / Hembra).
// Pura, sin React ni DB. Única fuente de verdad del desglose por sexo.
//
// Reglas (coinciden con el cálculo "general" actual del app):
// - Promedio de peso: solo animales con `peso > 0`.
// - `general` incluye a TODOS (con y sin sexo); los chips ♂/♀ solo computan
//   sobre los animales con sexo definido ('M' / 'H').
// - RECARAVANEADO u otros filtros de scope: el llamador los aplica antes de
//   pasar el array (igual que hoy).

import { Enums } from '../types/enums';
import { computeGdp } from './gdp';

export interface AnimalConSexo {
  sexo?: string | null;
  peso?: number | null;
}

/** Animal con caravana (necesaria para reclasificar por sexo actual). */
export interface AnimalConCaravana extends AnimalConSexo {
  caravana?: string | null;
}

export interface AnimalConGdp extends AnimalConSexo {
  pesajes?: Array<{ peso: number; fecha_evento: string }> | null;
  peso_inicial?: number | null;
  fecha_de_evento_peso_inicial?: string | null;
}

const isMacho = (a: AnimalConSexo): boolean => a.sexo === Enums.SEXO.M;
const isHembra = (a: AnimalConSexo): boolean => a.sexo === Enums.SEXO.H;
/** Sin sexo definido: ni macho ni hembra (incluye null/undefined/''). */
const isSinSexo = (a: AnimalConSexo): boolean => !isMacho(a) && !isHembra(a);

const tienePeso = (a: AnimalConSexo): boolean => typeof a.peso === 'number' && (a.peso as number) > 0;

function promedioPeso(animales: AnimalConSexo[]): number {
  const conPeso = animales.filter(tienePeso);
  if (conPeso.length === 0) return 0;
  return conPeso.reduce((sum, a) => sum + (a.peso as number), 0) / conPeso.length;
}

export interface PromedioPorSexo {
  /** Incluye a todos los animales con peso>0 (con y sin sexo). */
  general: number;
  macho: number;
  hembra: number;
  /** Peso promedio de los animales sin sexo definido. */
  sinSexo: number;
}

/** Peso promedio por sexo. `general` incluye a los animales sin sexo. */
export function calcularPesoPromedioPorSexo(animales: AnimalConSexo[] | null | undefined): PromedioPorSexo {
  if (!animales || animales.length === 0) return { general: 0, macho: 0, hembra: 0, sinSexo: 0 };
  return {
    general: promedioPeso(animales),
    macho: promedioPeso(animales.filter(isMacho)),
    hembra: promedioPeso(animales.filter(isHembra)),
    sinSexo: promedioPeso(animales.filter(isSinSexo)),
  };
}

/**
 * Reclasifica los animales por su sexo ACTUAL (de la tabla derivada `animales`,
 * fuente de verdad del sexo vigente), conservando el peso que ya traen (típicamente
 * el peso registrado en la sesión). El llamador construye `sexoPorCaravana` a partir
 * de `AnimalService.getAll()`.
 *
 * - Si la caravana no está en el mapa (animal dado de baja / recaravaneado desde),
 *   se conserva el sexo del registro original (mejor esfuerzo).
 * - sexo `null` en la tabla vigente se normaliza a `undefined` (cuenta como sin sexo).
 *
 * Es pura: no toca la DB ni React.
 */
export function reclasificarSexoPorCaravana<T extends AnimalConCaravana>(
  animales: T[] | null | undefined,
  sexoPorCaravana: Map<string, string | null | undefined>,
): T[] {
  const list = animales ?? [];
  return list.map(a => {
    const caravana = a.caravana;
    if (!caravana || !sexoPorCaravana.has(caravana)) return a; // fallback: sexo de sesión
    const sexoActual = sexoPorCaravana.get(caravana);
    return { ...a, sexo: sexoActual ?? undefined };
  });
}

export interface ConteoPorSexo {
  total: number;
  macho: number;
  hembra: number;
  sinSexo: number;
}

/** Conteo de animales por sexo. */
export function calcularConteoPorSexo(animales: AnimalConSexo[] | null | undefined): ConteoPorSexo {
  const list = animales ?? [];
  const macho = list.filter(isMacho).length;
  const hembra = list.filter(isHembra).length;
  return {
    total: list.length,
    macho,
    hembra,
    sinSexo: list.length - macho - hembra,
  };
}

export interface AnimalConUbicacion extends AnimalConSexo {
  ubicacion_id?: string | null;
}

function acumularConteo(c: ConteoPorSexo, sexo?: string | null): void {
  c.total++;
  if (sexo === Enums.SEXO.M) c.macho++;
  else if (sexo === Enums.SEXO.H) c.hembra++;
  else c.sinSexo++;
}

/**
 * Cuenta animales agrupados por ubicacion_id.
 * Devuelve un mapa ubicacionId → ConteoPorSexo. Ignora animales sin ubicacion_id.
 */
export function contarAnimalesPorUbicacion(animales: AnimalConUbicacion[] | null | undefined): Record<string, ConteoPorSexo> {
  const map: Record<string, ConteoPorSexo> = {};
  for (const a of animales ?? []) {
    const uid = a.ubicacion_id;
    if (!uid) continue;
    if (!map[uid]) map[uid] = { total: 0, macho: 0, hembra: 0, sinSexo: 0 };
    acumularConteo(map[uid], a.sexo);
  }
  return map;
}

/**
 * Cuenta animales agrupados por unidad_id (agregando todas sus ubicaciones).
 * Necesita la lista de ubicaciones para mapear ubicacion_id → unidad_id.
 */
export function contarAnimalesPorUnidad(
  animales: AnimalConUbicacion[] | null | undefined,
  ubicaciones: { id: string; unidad_id?: string | null }[] | null | undefined,
): Record<string, ConteoPorSexo> {
  const ubicToUnidad = new Map<string, string>();
  for (const u of ubicaciones ?? []) {
    if (u.id && u.unidad_id) ubicToUnidad.set(u.id, u.unidad_id);
  }
  const map: Record<string, ConteoPorSexo> = {};
  for (const a of animales ?? []) {
    const uid = a.ubicacion_id;
    if (!uid) continue;
    const unidadId = ubicToUnidad.get(uid);
    if (!unidadId) continue;
    if (!map[unidadId]) map[unidadId] = { total: 0, macho: 0, hembra: 0, sinSexo: 0 };
    acumularConteo(map[unidadId], a.sexo);
  }
  return map;
}

export interface GdpPorSexo {
  /** Promedio de GDP de todos los animales con GDP válido (con y sin sexo). */
  general: number | null;
  macho: number | null;
  hembra: number | null;
  /** GDP promedio de los animales sin sexo definido. null si ninguno tiene GDP válido. */
  sinSexo: number | null;
}

/** GDP promedio (kg/día) por sexo. null si ningún animal del sexo tiene GDP válido. */
export function calcularGdpPorSexo(animales: AnimalConGdp[] | null | undefined): GdpPorSexo {
  const list = animales ?? [];
  const extractGdps = (arr: AnimalConGdp[]): number[] =>
    arr
      .map(a => computeGdp(a.pesajes ?? undefined, a.peso_inicial ?? undefined, a.fecha_de_evento_peso_inicial ?? undefined))
      .map(g => (g ? g.gdp : null))
      .filter((g): g is number => typeof g === 'number');
  const avg = (arr: number[]): number | null => (arr.length === 0 ? null : arr.reduce((s, g) => s + g, 0) / arr.length);
  return {
    general: avg(extractGdps(list)),
    macho: avg(extractGdps(list.filter(isMacho))),
    hembra: avg(extractGdps(list.filter(isHembra))),
    sinSexo: avg(extractGdps(list.filter(isSinSexo))),
  };
}
